import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Instagram, Globe, Mail, Phone } from 'lucide-react';

const PODCAST_LINKS = [
  { label: 'iHeartRadio', href: 'https://iheart.com/podcast/331039271/' },
  { label: 'Spotify', href: 'https://open.spotify.com/show/4jY5GZY8pexXwjJ5J5KPlt?si=hsrmhXtyRQao593yfM9fCg' },
  { label: 'Apple Podcast', href: 'https://podcasts.apple.com/us/podcast/professional-chameleon/id1895020478' },
  { label: 'YouTube', href: 'https://youtube.com/professionalchameleon' },
  { label: 'Amazon Music', href: 'https://music.amazon.com/podcasts/bd906130-be59-45cf-aa20-60cbd33461ee/professional-chameleon' },
];

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const toggleLang = () => i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');

  return (
    <footer className="bg-[#233559] dark:bg-[#0F1C2E] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Logo & tagline */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center mb-4 group">
              <img
                src="/logo-dark-coral.svg"
                alt="Mikel Media"
                className="h-14 w-auto transition-opacity group-hover:opacity-80"
              />
            </Link>
            <p className="text-white/50 font-source-sans text-sm leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-3 mt-5">
              <a href="https://instagram.com/mikelmedia" target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#7D468C] hover:text-[#7D468C] transition-colors">
                <Instagram size={15} />
              </a>
              <a href="https://mikelmedia.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#4EBFBF] hover:text-[#4EBFBF] transition-colors">
                <Globe size={15} />
              </a>
              <a href="mailto:mikel@mikel.media" className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#F2785C] hover:text-[#F2785C] transition-colors">
                <Mail size={15} />
              </a>
            </div>
          </div>

          {/* Nav links */}
          <div>
            <h4 className="font-poppins font-bold text-sm mb-4 text-white/90 tracking-wider uppercase">{t('footer.nav')}</h4>
            <ul className="space-y-2 font-source-sans text-sm text-white/50">
              {[
                { label: t('nav.home'), to: '/' },
                { label: t('nav.portfolio'), to: '/portafolio' },
                { label: t('nav.courses'), to: '/cursos' },
                { label: t('nav.podcast'), to: '/podcast' },
                { label: t('nav.blog'), to: '/blog' },
                { label: t('nav.about'), to: '/acerca' },
                { label: t('nav.contact'), to: '/contacto' },
              ].map(l => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-poppins font-bold text-sm mb-4 text-white/90 tracking-wider uppercase">{t('footer.contact_label')}</h4>
            <div className="space-y-2 font-source-sans text-sm text-white/50">
              <div className="flex items-center gap-2"><Mail size={13} className="text-[#4EBFBF]" /><span>mikel@mikel.media</span></div>
              <div className="flex items-center gap-2"><Phone size={13} className="text-[#4EBFBF]" /><span>+1 787 422 2734</span></div>
              <div className="flex items-center gap-2"><Instagram size={13} className="text-[#4EBFBF]" /><span>@mikelmedia</span></div>
              <div className="flex items-center gap-2"><Globe size={13} className="text-[#4EBFBF]" /><span>Puerto Rico & USA</span></div>
            </div>

            <div className="mt-6">
              <h5 className="font-poppins font-bold text-xs mb-3 text-white/85 tracking-wider uppercase">Podcast</h5>
              <ul className="space-y-1.5 text-sm font-source-sans text-white/55">
                {PODCAST_LINKS.map(link => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-[#4EBFBF] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={toggleLang}
                data-testid="footer-lang-toggle"
                className="text-xs font-bold font-poppins text-white/50 hover:text-white border border-white/20 px-3 py-1 transition-colors hover:border-white/50"
              >
                {i18n.language === 'es' ? 'EN' : 'ES'}
              </button>
              <button
                onClick={toggleTheme}
                data-testid="footer-theme-toggle"
                className="p-1.5 border border-white/20 text-white/50 hover:text-white hover:border-white/50 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex gap-2">
            <div className="w-3 h-3 rotate-45 bg-[#7D468C]/60" />
            <div className="w-3 h-3 rotate-45 border border-[#4EBFBF]/50" />
            <div className="w-3 h-3 rotate-45 bg-[#F2785C]/50" />
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-x-5 gap-y-2">
            {/* Static pages served outside the React router, so plain anchors, not <Link>. */}
            <nav aria-label="Legal" className="flex gap-5 text-xs font-source-sans text-white/50">
              <a href="/privacidad/" className="hover:text-white transition-colors" data-testid="footer-privacy">
                {i18n.language === 'es' ? 'Política de privacidad' : 'Privacy Policy'}
              </a>
              <a href="/terminos/" className="hover:text-white transition-colors" data-testid="footer-terms">
                {i18n.language === 'es' ? 'Términos y condiciones' : 'Terms'}
              </a>
            </nav>
            <p className="text-white/30 text-xs font-source-sans">{t('footer.copyright')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
